"use client";

import { useEffect } from "react";

export function useScrollReveal() {
  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const targets = Array.from(
      document.querySelectorAll<HTMLElement>(
        "[data-reveal], [data-reveal-stagger]",
      ),
    );

    if (reduced) {
      targets.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      // Fire well before elements reach the viewport so scroll-triggered
      // animations are smoothly armed, and so static / non-scrolling
      // contexts (screenshots, SSR snapshots, slow devices) still reveal.
      { threshold: 0, rootMargin: "0px 0px -5% 0px" },
    );

    targets.forEach((el) => observer.observe(el));

    // Defensive fallback: if for any reason the observer hasn't revealed
    // an element after 1.5s, force it visible. Better to skip the
    // animation than leave content invisible.
    const fallback = window.setTimeout(() => {
      targets.forEach((el) => {
        if (!el.classList.contains("is-visible")) {
          el.classList.add("is-visible");
        }
      });
    }, 1500);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);
}
